import { useEffect, useRef, useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function MethodPage() {
  const [isVisible, setIsVisible] = useState<{ [key: string]: boolean }>({});
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    Object.keys(sectionRefs.current).forEach((key) => {
      const element = sectionRefs.current[key];
      if (element) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible((prev) => ({ ...prev, [key]: true }));
            }
          },
          { threshold: 0.1 }
        );
        observer.observe(element);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <div className="bg-sacred-moss">
      <Navigation />

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Deep Burgundy vignette */}
        <div 
          className="absolute inset-0" 
          style={{
            background: 'radial-gradient(circle at 0% 0%, #3D0C1A 0%, transparent 50%), radial-gradient(circle at 100% 0%, #3D0C1A 0%, transparent 50%), radial-gradient(circle at 0% 100%, #3D0C1A 0%, transparent 50%), radial-gradient(circle at 100% 100%, #3D0C1A 0%, transparent 50%)'
          }}
        ></div>

        {/* Botanical corner accents */}
        <div className="absolute top-8 left-8 w-32 h-32 opacity-[0.08]">
          <svg viewBox="0 0 100 100" className="w-full h-full text-ancient-gold">
            <path
              d="M10 10 Q30 20 50 10 Q60 30 50 50 Q30 60 10 50 Q20 30 10 10"
              stroke="currentColor"
              fill="none"
              strokeWidth="0.5"
            />
          </svg>
        </div>

        <div className="relative z-10 text-center px-8 max-w-5xl mx-auto">
          <h1 className="font-cormorant text-ancient-gold tracking-widest-plus text-5xl md:text-6xl mb-8">
            THE METHOD
          </h1>
          <div className="w-32 h-px bg-ancient-gold mx-auto mb-12"></div>
          
          <p className="font-cormorant italic text-moon-ivory text-2xl md:text-3xl leading-relaxed">
            This is not a framework. This is a return.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* PHILOSOPHY SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['philosophy'] = el; }}
        className={`bg-moon-ivory py-24 px-8 transition-all duration-1200 ${
          isVisible['philosophy'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="font-cormorant text-deep-burgundy tracking-widest-plus text-3xl mb-12 text-center">
            THE PHILOSOPHY
          </h2>

          <div className="space-y-8">
            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose text-center">
              <span className="font-cormorant text-ancient-gold text-6xl float-left mr-4 leading-none">M</span>
              ost transformation work operates from the assumption that you are broken and need to be fixed. That there is something wrong with you that needs to be corrected, healed, or optimized.
            </p>

            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose text-center">
              The House of Selkara Method operates from a different premise: You are not broken. You are buried.
            </p>

            <p className="font-garamond text-[#2A2A2A] text-lg leading-loose text-center">
              Underneath everything life has layered on top of you — the conditioning, the expectations, the performance — there is a version of you that has been waiting. She is not damaged. She is dormant. And this work is about excavating her.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* THE THREE PHASES SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['phases'] = el; }}
        className={`bg-deep-burgundy py-24 px-8 relative overflow-hidden transition-all duration-1200 ${
          isVisible['phases'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Botanical corner accent */}
        <div className="absolute bottom-8 right-8 w-32 h-32 opacity-[0.08]">
          <svg viewBox="0 0 100 100" className="w-full h-full text-ancient-gold">
            <path
              d="M50 90 Q60 70 50 50 Q30 40 10 50 M50 90 Q70 80 90 90"
              stroke="currentColor"
              fill="none"
              strokeWidth="0.5"
            />
          </svg>
        </div>

        <div className="max-w-6xl mx-auto">
          <h2 className="font-cormorant text-ancient-gold tracking-widest-plus text-3xl mb-16 text-center">
            THE THREE PHASES
          </h2>

          <div className="space-y-16">
            {/* Phase 1 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
              <div className="text-center md:text-right">
                <div className="inline-block mb-4">
                  <svg className="w-16 h-16 text-ancient-gold mx-auto md:ml-auto" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C11.45 2 11 2.45 11 3C11 3.55 11.45 4 12 4C12.55 4 13 3.55 13 3C13 2.45 12.55 2 12 2M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6M12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8Z"/>
                  </svg>
                </div>
                <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-2xl mb-4">
                  PHASE ONE
                </h3>
                <p className="font-cormorant text-blush-ash italic text-lg">
                  The Diagnosis
                </p>
              </div>

              <div className="md:col-span-2">
                <p className="font-garamond text-moon-ivory text-base leading-relaxed mb-4">
                  We begin by seeing what is actually happening — not what you think is happening, not what you have been told is happening, but what is true underneath the performance.
                </p>
                <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                  This is diagnostic work. I listen for what is not being said. I see the patterns you cannot yet name. I identify where the performance ends and where you begin. This phase is about clarity — ruthless, uncomfortable, necessary clarity.
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-ancient-gold opacity-20"></div>

            {/* Phase 2 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
              <div className="text-center md:text-right">
                <div className="inline-block mb-4">
                  <svg className="w-16 h-16 text-ancient-gold mx-auto md:ml-auto" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C11.45 2 11 2.45 11 3C11 3.55 11.45 4 12 4C12.55 4 13 3.55 13 3C13 2.45 12.55 2 12 2M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6M12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8Z"/>
                  </svg>
                </div>
                <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-2xl mb-4">
                  PHASE TWO
                </h3>
                <p className="font-cormorant text-blush-ash italic text-lg">
                  The Excavation
                </p>
              </div>

              <div className="md:col-span-2">
                <p className="font-garamond text-moon-ivory text-base leading-relaxed mb-4">
                  Once we know what is true, we begin the work of excavation. This is where we go back for the version of you that has been buried. The one who existed before the world had opinions. Before you learned to shrink.
                </p>
                <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                  This phase is embodied. It is not intellectual. It is not about understanding. It is about feeling, remembering, and reclaiming. We work with what has been hidden — the rage, the grief, the power — and we bring it into the light.
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-ancient-gold opacity-20"></div>

            {/* Phase 3 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
              <div className="text-center md:text-right">
                <div className="inline-block mb-4">
                  <svg className="w-16 h-16 text-ancient-gold mx-auto md:ml-auto" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C11.45 2 11 2.45 11 3C11 3.55 11.45 4 12 4C12.55 4 13 3.55 13 3C13 2.45 12.55 2 12 2M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6M12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8Z"/>
                  </svg>
                </div>
                <h3 className="font-cormorant text-ancient-gold tracking-widest-plus text-2xl mb-4">
                  PHASE THREE
                </h3>
                <p className="font-cormorant text-blush-ash italic text-lg">
                  The Integration
                </p>
              </div>

              <div className="md:col-span-2">
                <p className="font-garamond text-moon-ivory text-base leading-relaxed mb-4">
                  The final phase is about integration. This is where we take everything that has been excavated and we build a life around it. Not a performance. Not a persona. A life that is aligned with who you actually are.
                </p>
                <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                  This is where sovereignty becomes embodied. Where you stop performing and start arriving. Where you stop shrinking and start taking up space. Where you become undeniable — not because you are trying, but because you are finally, fully yourself.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* PRINCIPLES SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['principles'] = el; }}
        className={`bg-sacred-moss py-24 px-8 transition-all duration-1200 ${
          isVisible['principles'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="font-cormorant text-ancient-gold tracking-widest-plus text-3xl mb-16 text-center">
            THE PRINCIPLES
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="font-cormorant text-ancient-gold tracking-wider text-xl mb-4">
                You Are Not Broken
              </h3>
              <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                The work does not begin with the assumption that something is wrong with you. It begins with the understanding that you have been performing a version of yourself that is not true — and that underneath that performance, you are whole.
              </p>
            </div>

            <div>
              <h3 className="font-cormorant text-ancient-gold tracking-wider text-xl mb-4">
                The Body Knows
              </h3>
              <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                This is embodied work. Your body holds the truth that your mind has been trained to ignore. We work with sensation, not just story. We listen to what your body is saying — and we trust it.
              </p>
            </div>

            <div>
              <h3 className="font-cormorant text-ancient-gold tracking-wider text-xl mb-4">
                Sovereignty Is Not Given
              </h3>
              <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                You do not need to be empowered. You need to remember that you already have power. Sovereignty is not something I give you. It is something you reclaim.
              </p>
            </div>

            <div>
              <h3 className="font-cormorant text-ancient-gold tracking-wider text-xl mb-4">
                This Work Is Not Comfortable
              </h3>
              <p className="font-garamond text-moon-ivory text-base leading-relaxed">
                Excavation is not gentle. It is necessary. If you are looking for comfort, this is not the place. If you are looking for truth, you are home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full h-px bg-ancient-gold opacity-30"></div>

      {/* CTA SECTION */}
      <section 
        ref={(el) => { sectionRefs.current['cta'] = el; }}
        className={`bg-moon-ivory py-24 px-8 transition-all duration-1200 ${
          isVisible['cta'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-cormorant italic text-deep-burgundy text-3xl md:text-4xl leading-relaxed mb-12">
            Ready to begin the return?
          </h2>

          <button
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="inline-block border-2 border-deep-burgundy text-deep-burgundy font-cormorant tracking-widest-plus px-16 py-5 hover:bg-deep-burgundy hover:text-moon-ivory transition-all duration-500 cursor-pointer whitespace-nowrap"
          >
            EXPLORE OFFERINGS
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}